import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Upload, Loader2, CheckCircle2, FileText, Building2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Dialog : Scanner une facture fournisseur via OCR Vision IA.
 * Étape 1 : prendre/choisir photo
 * Étape 2 : revue des champs extraits + validation
 * Étape 3 : confirmation + redirection
 */
export default function ScanFactureFournisseurDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [factureId, setFactureId] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [docType, setDocType] = useState('autre');
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1);
    setScanning(false);
    setExtracted(null);
    setPhotoUrl(null);
    setFactureId(null);
    setConfidence(0);
    setDocType('autre');
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Image uniquement (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image > 15 Mo');
      return;
    }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('apply', 'true');
      // Endpoint universel (auto-détection facture / ticket / autoroute / carburant / BL)
      const r = await api.post('/factures-fournisseurs/ocr-ticket', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = r.data;
      setConfidence(data.confidence || 0);
      setExtracted(data.extracted || {});
      setPhotoUrl(data.photo_url);
      setFactureId(data.record_id);
      setDocType(data.type || 'autre');

      if (data.success && data.confidence >= 30) {
        const typeLabels = {
          facture: 'Facture',
          ticket_cb: 'Ticket CB',
          ticket_caisse: 'Ticket de caisse',
          ticket_carburant: 'Ticket carburant',
          ticket_autoroute: 'Ticket autoroute',
          ticket_restaurant: 'Addition restaurant',
          ticket_parking: 'Ticket parking',
          bon_livraison: 'Bon de livraison',
          autre: 'Justificatif',
        };
        toast.success(`${typeLabels[data.type] || 'Document'} lu (confiance ${data.confidence}%)`);
        setStep(2);
      } else {
        toast.error('Document illisible. Refais une photo en bonne lumière.');
        setStep(1);
      }
    } catch (err) {
      console.error('OCR err:', err);
      toast.error(err.response?.data?.detail || 'Erreur OCR');
    } finally {
      setScanning(false);
    }
  };

  const handleValidate = () => {
    toast.success('Facture enregistrée');
    onCreated?.(factureId);
    handleClose();
  };

  const fmt = (v) => (v == null || v === '' ? '—' : v);
  const fmtMoney = (v) => (v == null ? '—' : `${parseFloat(v).toFixed(2)} €`);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="scan-facture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Scanner justificatif (IA)
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
              <p className="font-medium mb-2">📷 Photographie n'importe quel justificatif comptable</p>
              <p className="text-xs">L'IA détecte automatiquement le type : <strong>facture fournisseur, ticket CB, ticket carburant, péage autoroute, parking, restaurant, bon de livraison...</strong> et extrait fournisseur, montants HT/TVA/TTC, et code comptable.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="aspect-square rounded-xl border-2 border-dashed border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 flex flex-col items-center justify-center cursor-pointer transition-colors"
                data-testid="scan-take-photo">
                <Camera className="w-12 h-12 text-emerald-600 mb-2" />
                <span className="font-semibold text-slate-700 dark:text-slate-200">{scanning ? 'Lecture…' : 'Prendre photo'}</span>
                <span className="text-xs text-slate-500 mt-1">Caméra mobile</span>
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  disabled={scanning} />
              </label>

              <label className="aspect-square rounded-xl border-2 border-dashed border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 flex flex-col items-center justify-center cursor-pointer transition-colors"
                data-testid="scan-upload-file">
                <Upload className="w-12 h-12 text-blue-600 mb-2" />
                <span className="font-semibold text-slate-700 dark:text-slate-200">Choisir fichier</span>
                <span className="text-xs text-slate-500 mt-1">Galerie / PC</span>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  disabled={scanning} />
              </label>
            </div>

            {scanning && (
              <div className="text-center py-3">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600 inline-block mr-2" />
                <span className="text-sm text-slate-600">Analyse de la facture par l'IA…</span>
              </div>
            )}
          </div>
        )}

        {step === 2 && extracted && (
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 rounded-lg p-3 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div className="text-xs flex-1">
                <strong>Type détecté : {(docType || 'autre').replace('_', ' ').toUpperCase()}</strong> · Confiance {confidence}%
                <p className="text-slate-600 dark:text-slate-400 mt-0.5">Vérifie les champs extraits, le justificatif est en brouillon.</p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2 text-sm text-slate-700">
                <Building2 className="w-4 h-4" /> Marchand / Fournisseur
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label className="text-xs">Nom</Label><div className="font-medium">{fmt(extracted.marchand_nom || extracted.fournisseur_nom)}</div></div>
                <div><Label className="text-xs">SIRET</Label><div className="font-medium font-mono text-xs">{fmt(extracted.marchand_siret || extracted.fournisseur_siret)}</div></div>
                {extracted.marchand_ville && (
                  <div className="col-span-2"><Label className="text-xs">Ville</Label><div className="font-medium">{extracted.marchand_ville}</div></div>
                )}
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t">
              <h4 className="font-semibold text-sm text-slate-700">
                {docType === 'facture' || docType === 'bon_livraison' ? 'Document' : 'Ticket'}
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {extracted.numero_document && <div><Label className="text-xs">N°</Label><div className="font-medium">{extracted.numero_document}</div></div>}
                <div><Label className="text-xs">Date</Label><div className="font-medium">{fmt(extracted.date)}{extracted.heure ? ` ${extracted.heure}` : ''}</div></div>
                {extracted.date_echeance && <div><Label className="text-xs">Échéance</Label><div className="font-medium">{extracted.date_echeance}</div></div>}
                {extracted.moyen_paiement && <div><Label className="text-xs">Paiement</Label><div className="font-medium">{extracted.moyen_paiement}</div></div>}
                {extracted.categorie && <div><Label className="text-xs">Catégorie</Label><div className="font-medium">{extracted.categorie}</div></div>}
              </div>
            </div>

            {/* Détails spécifiques au type */}
            {extracted.details_specifiques && Object.values(extracted.details_specifiques || {}).some(v => v != null) && (
              <div className="space-y-2 pt-2 border-t">
                <h4 className="font-semibold text-sm text-slate-700">Détails {docType.replace('ticket_', '')}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {extracted.details_specifiques.carburant_type && <div>⛽ {extracted.details_specifiques.carburant_type}</div>}
                  {extracted.details_specifiques.carburant_litres && <div>{extracted.details_specifiques.carburant_litres} L</div>}
                  {extracted.details_specifiques.carburant_prix_litre && <div>{extracted.details_specifiques.carburant_prix_litre} €/L</div>}
                  {extracted.details_specifiques.autoroute_entree && <div>🛣️ {extracted.details_specifiques.autoroute_entree} → {extracted.details_specifiques.autoroute_sortie || '?'}</div>}
                  {extracted.details_specifiques.autoroute_distance_km && <div>{extracted.details_specifiques.autoroute_distance_km} km</div>}
                  {extracted.details_specifiques.parking_duree && <div>🅿️ {extracted.details_specifiques.parking_duree}</div>}
                  {extracted.details_specifiques.vehicule_immat && <div>🚗 {extracted.details_specifiques.vehicule_immat}</div>}
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2 border-t">
              <h4 className="font-semibold text-sm text-slate-700">Montants</h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded p-2 text-center">
                  <Label className="text-xs">HT</Label>
                  <div className="font-bold text-slate-900 dark:text-slate-100">{fmtMoney(extracted.montant_ht)}</div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded p-2 text-center">
                  <Label className="text-xs">TVA{extracted.taux_tva ? ` ${extracted.taux_tva}%` : ''}</Label>
                  <div className="font-bold text-slate-900 dark:text-slate-100">{fmtMoney(extracted.montant_tva)}</div>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded p-2 text-center">
                  <Label className="text-xs">TTC</Label>
                  <div className="font-bold text-emerald-700 dark:text-emerald-400">{fmtMoney(extracted.montant_ttc)}</div>
                </div>
              </div>
            </div>

            {extracted.code_comptable_suggere && (
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded p-2 text-xs">
                💼 Code comptable suggéré : <strong className="font-mono">{extracted.code_comptable_suggere}</strong>
              </div>
            )}

            {photoUrl && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 underline">Voir la photo originale</a>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="scan-cancel">
            {step === 2 ? 'Fermer' : 'Annuler'}
          </Button>
          {step === 2 && (
            <Button onClick={handleValidate} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="scan-validate">
              <CheckCircle2 className="w-4 h-4 mr-2" /> OK, valider en comptabilité
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
